import { Link } from "react-router-dom";
import { useArticlePath } from "@/lib/i18n";
import { trackEvent } from "@/hooks/useArticleAnalytics";

type Node = { slug: string; title: string; published_at: string; current: boolean };

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const Dot = ({ current }: { current: boolean }) => (
  <span aria-hidden className={`block h-[15px] w-[15px] rounded-full border-2 ${
    current ? "border-[#00E5FF] bg-[#34D399]" : "border-[#A78BFA]/70 bg-[#0a0b10]"}`} />
);

export const FollowTheStory = ({ thread }: { thread?: Node[] }) => {
  const toArticle = useArticlePath();
  if (!thread || thread.length < 3) return null;
  const nodes = thread.slice(-4);
  return (
    <section data-testid="follow-the-story" data-track="follow_the_story"
      className="mt-12 rounded-xl border border-white/[0.08] bg-[#0a0b10] p-5 sm:p-6">
      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-gray-400">Follow the Story</p>

      <div className="hidden sm:block relative mt-6">
        <span aria-hidden className="absolute top-[7px] left-2 right-10 h-px bg-gradient-to-r from-[#7C3AED]/50 via-white/15 to-[#00E5FF]/60" />
        <ol className="relative grid gap-x-4" style={{ gridTemplateColumns: `repeat(${nodes.length}, minmax(0, 1fr))` }}>
          {nodes.map((n, i) => (
            <li key={n.slug}>
              <Link to={toArticle(n.slug)} data-testid={`story-node-${i}`} className="group block"
                onClick={() => trackEvent("follow_story_click", { slug: n.slug })}>
                <Dot current={n.current} />
                <span className={`mt-2.5 block font-mono text-[11px] ${n.current ? "text-[#00E5FF] font-bold" : "text-gray-500"}`}>
                  {fmtDate(n.published_at)}
                </span>
                <span className={`mt-1 block text-[12px] leading-snug line-clamp-2 pr-3 transition-colors ${
                  n.current ? "text-white font-semibold" : "text-gray-400 group-hover:text-gray-200"}`}>
                  {n.title}
                </span>
              </Link>
            </li>
          ))}
        </ol>
      </div>

      <ol className="sm:hidden relative mt-5 ml-[7px] space-y-5 border-l border-white/10">
        {nodes.map((n, i) => (
          <li key={n.slug} className="relative pl-5">
            <span className="absolute -left-[8px] top-0.5"><Dot current={n.current} /></span>
            <Link to={toArticle(n.slug)} data-testid={`story-node-mobile-${i}`}
              onClick={() => trackEvent("follow_story_click", { slug: n.slug })}>
              <span className={`block font-mono text-[11px] ${n.current ? "text-[#00E5FF] font-bold" : "text-gray-500"}`}>
                {fmtDate(n.published_at)}
              </span>
              <span className={`mt-0.5 block text-[13px] leading-snug line-clamp-2 ${
                n.current ? "text-white font-semibold" : "text-gray-400"}`}>
                {n.title}
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </section>
  );
};
